import { formatShortDate } from "@/lib/utils";
import React from "react";
import { MethodPill } from "./MethodPill";
import { PaymentRecord } from "@/types/order-type";

type PaymentRecordType = "CHARGE" | "REFUND";

type Props = {
  payment: PaymentRecord;
};

export default function PaymentRecordLine({ payment }: Props) {
  const isRefund = false;

  // Who did it
  const actor =
    payment.createdByUsername ?? "System";

  // Avatar initials
  const initials = actor
    .split(" ")
    .filter(Boolean)
    .map((p) => p[0])
    .join("")
    .substring(0, 2)
    .toUpperCase();

  const amountLabel = payment.amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return (
    <div key={String(payment.id)} className="flex items-start gap-2">
      {/* Avatar */}
      <div className="mt-1 h-7 w-7 rounded-full bg-slate-100 flex items-center justify-center text-[10px] font-semibold text-slate-600">
        {initials}
      </div>

      <div className="flex-1 min-w-0">
        {/* Bubble */}
        <div
          className={[
            "inline-flex max-w-full rounded-2xl border px-3 py-2 shadow-sm",
            isRefund
              ? "border-rose-200 bg-rose-50"
              : "border-slate-200 bg-slate-50",
          ].join(" ")}
        >
          <div className="flex flex-col gap-1 min-w-0 w-full">
            {/* Top row */}
            <div className="flex items-center justify-between gap-2 min-w-0">
              <div className="flex items-center gap-1.5 sm:gap-2 min-w-0 flex-wrap">
                <MethodPill
                  method={payment.method as "CASH" | "BANK" | "BAKONG"}
                  bank={payment.bankName || ""}
                />

                {/* Charge / Refund pill */}
                <span
                  className={[
                    "inline-flex items-center rounded-full px-1.5 sm:px-2 py-[2px] text-[10px] font-medium shrink-0",
                    isRefund
                      ? "bg-rose-100 text-rose-700"
                      : "bg-emerald-100 text-emerald-700",
                  ].join(" ")}
                >
                  {isRefund ? "Refund" : "Payment"}
                </span>

                {/* Optional status chip */}
                {payment.status && (
                  <span className="inline-flex items-center rounded-full bg-slate-200/80 px-1.5 sm:px-2 py-[2px] text-[10px] font-medium text-slate-700 shrink-0">
                    {payment.status}
                  </span>
                )}

                {payment.providerRef && (
                  <span className="truncate text-[10px] text-slate-400 font-mono min-w-0  sm:max-w-none">
                    Ref: {payment.providerRef}
                  </span>
                )}
              </div>

              {/* Amount */}
              <div
                className={[
                  "shrink-0 text-xs font-semibold whitespace-nowrap",
                  isRefund ? "text-rose-600" : "text-emerald-600",
                ].join(" ")}
              >
                {isRefund ? `- ${amountLabel}` : amountLabel}
              </div>
            </div>

            {/* Note */}
            {payment.note && (
              <div className="text-[11px] text-slate-600 truncate">
                {payment.note}
              </div>
            )}
          </div>
        </div>

        {/* Meta line */}
        <div className="mt-1 flex items-center gap-2 text-[10px] text-slate-400">
          <span className="truncate">
            {isRefund ? "Refunded by " : "Captured by "}
            <span className="font-medium text-slate-500">{actor}</span>
          </span>
          <span>•</span>
          <span>
            {formatShortDate(payment.capturedAt ?? payment.createdAt ?? null)}
          </span>
        </div>
      </div>
    </div>
  );
}
